// Sample world for the Cardboardmarket web app. Fictional traders, listings and meetups.
//
// NOTE: `verified` is retained on traders and shops but is deliberately unused.
// Verification is parked, so nothing renders a badge or filters on it — see the
// backend plan. The field stays because the schema keeps the column.
// Puerta del Sol. The product launches in Spain; the fixtures used to sit in
// Belgium, left over from the original mockup.
export const CENTER = [40.4168, -3.7038];
/**
 * What is left here is REFERENCE DATA, not content.
 *
 * Every invented trader, listing, shop, café and conversation has been deleted.
 * They were the original mockup's furniture, and showing a real person a made-up
 * shop with made-up opening hours is not a placeholder — it is a false statement
 * they might act on. Nothing in this file describes a person or a place any more.
 */

/**
 * Games, as a code the API understands plus a label a person reads.
 *
 * These were display strings only, which meant the UI filtered on "Pokémon" while
 * the API answers "pokemon" — a filter that silently matched nothing. The code is
 * the identity; the label is presentation.
 */
export const GAMES = [
  { id: 'onepiece', label: 'One Piece' },
  { id: 'pokemon', label: 'Pokémon' },
  { id: 'magic', label: 'Magic' },
  { id: 'yugioh', label: 'Yu-Gi-Oh!' },
  { id: 'lorcana', label: 'Lorcana' },
  // Riot's League of Legends card game. Last in the row because it is the
  // newest and the shallowest catalogue, not because it matters least.
  { id: 'riftbound', label: 'Riftbound' },
  // Bandai's, and the same reasoning puts it last: 37 expansions on CardTrader
  // against One Piece's hundreds. The label is just "Gundam" because that is what
  // both sources call it and what somebody holding one would search for.
  { id: 'gundam', label: 'Gundam' },
];
export const gameLabel = (id) => (GAMES.find((g) => g.id === id) || {}).label || id;
/*
 * The ids as a set: THE test for "a game the product knows", wherever a game
 * arrives from outside the code — an address, the device's memory, a prop.
 * One idiom, so the shell and the posting form cannot disagree about it.
 */
export const KNOWN_GAMES = new Set(GAMES.map((g) => g.id));
export const GRADES = ['M', 'NM', 'EX', 'GD', 'LP', 'PL', 'PO'];



// Cards the signed-in trader owns and can offer in a trade.






/**
 * How far a trader will go, as ONE ladder.
 *
 * The four rungs were written out inline in five different Selects — browse's
 * rail, nearby's head, the location dialog, onboarding and the profile — and
 * separately assumed by the "Widen to 15 km" buttons on browse and nearby, which
 * had the middle rung hard-coded into their labels. Six places holding the same
 * list is not duplication so much as six chances to disagree, and they already
 * did: at 15 km the button offered the radius you were on and did nothing, and
 * at 50 km it said "widen" while narrowing you to 15 and losing results.
 *
 * Ordered, ascending, and that order is load-bearing: `widerRadius` walks it.
 */
export const RADIUS_OPTIONS = ['1 km', '5 km', '15 km', '50 km'];

/**
 * How many places a trader may save. The API is the authority; this is what the
 * interface needs in order to stop offering an Add button that would be refused.
 *
 * The number lives in `api/src/lib/locations.ts` and in migration 052's trigger,
 * and now here as well — three copies, each with a different job: the message a
 * person reads, the floor a race cannot get past, and a button that is not
 * offered when it cannot work. A refusal the client could have predicted and did
 * not is a worse interface than a control that quietly stops being offered.
 */
export const MAX_LOCATIONS = 5;

/**
 * The next rung ABOVE a radius, or undefined when there is no such thing.
 *
 * Compared as a number rather than looked up by index, for two reasons. An
 * account stores metres, and `store.jsx` turns `radiusM` into `"3 km"` for
 * anything that is not a rung — `indexOf` returns -1 there, and -1 + 1 is the
 * FIRST rung, so an index lookup would answer "widen to 1 km". And strictly
 * greater than is the property the caller actually needs: whatever comes back
 * is wider than what you have, which is what the button promises.
 *
 * Undefined at the top is the answer, not a missing one. A caller renders no
 * widen button rather than a disabled one — an action you cannot take is worse
 * than no action, because it still reads as the way forward.
 */
export const widerRadius = (current) => {
  const km = parseFloat(current);
  if (!Number.isFinite(km)) return undefined;
  return RADIUS_OPTIONS.find((option) => parseFloat(option) > km);
};

/*
 * The 1 km floor is a privacy rule, not a formatting preference: a sub-kilometre
 * distance is a strong locator. Below it we report the band, never a figure.
 *
 * The band's WORDS are the caller's, because this file has no translator and must
 * not have one — a t() at module scope runs once, before any locale is known, and
 * freezes the string in whichever language loaded first. So the caller passes
 * `t("less than 1 km")`, written out as the literal it is so that
 * `check-translations.mjs` can see it, and the English below is what a caller
 * that says nothing gets — exactly as DistanceTag has always worked.
 */
export const fmtKm = (km, floorLabel = 'less than 1 km') => (km == null || km < 1 ? floorLabel : km.toFixed(1) + ' km');
export const GRADE_NAMES = { M: 'Mint', NM: 'Near mint', EX: 'Excellent', GD: 'Good', LP: 'Light play', PL: 'Played', PO: 'Poor' };

/*
 * The badge on a listing card, as English KEYS.
 *
 * ListingCard draws the badge and picks its tone from `mode`; the words come from
 * here. Sixty cards render at once on browse, so this was sixty English badges
 * sitting directly under tabs that already read "Todo / En venta / Para cambiar" —
 * the same three words twice on one screen, in two languages, one of them wrong.
 *
 * "For trade" and not the design system's own "Trade": the browse tab, the badge
 * on the listing page and this card are one thing said in three places, and they
 * were three different English words before they were one wrong language. Both
 * keys already had Spanish; nobody had a way to reach it.
 */
export const LISTING_MODE_NAMES = { sale: 'For sale', trade: 'For trade' };

/*
 * The status badge on a meetup card, as English KEYS.
 *
 * MeetupCard draws the badge and picks its tone from `status`; the words come
 * from here through `statusLabel`. They lived inside the design system until
 * now, hardcoded in English, and every Spanish reader saw them that way on both
 * the Trades screen and in a conversation — invisibly, because `t()` was never
 * involved and `check-translations.mjs` only reads `app/`.
 *
 * Keyed by the CARD's vocabulary rather than the API's state machine: `TRADE_STATE`
 * in api.jsx maps one to the other, and this table is the far end of that map.
 */
export const MEETUP_STATUS_NAMES = {
  proposed: 'Awaiting reply',
  confirmed: 'Confirmed',
  done: 'Completed',
  cancelled: 'Cancelled',
};

/*
 * The languages a card can be printed in — the `card_language` enum, in the same
 * order it is declared in.
 *
 * The first eleven are migration 049's, which is Cardmarket's own order. The six
 * after them arrived with migration 053 and are appended rather than sorted in,
 * because Postgres orders an enum by declaration and inserting would renumber
 * everything already stored. Nothing anybody SEES is in this order: every picker
 * sorts by the name the reader sees, in the reader's own language.
 *
 * The tags are the stored values and never reach a screen; the names beside them
 * are English KEYS that go through t(), so a Spanish reader sees "Japonés" on a
 * Japanese card. The card's language and the reader's language are two different
 * things and this is the one place they meet.
 *
 * KEEP BOTH IN STEP WITH `api/src/lib/languages.ts`, and keep every tag in the
 * first list named in the second. A tag the API accepts and this file has no
 * name for renders as a raw "nl" on a listing; a tag in CARD_LANGUAGES with no
 * entry in CARD_LANGUAGE_NAMES is worse than that — t(undefined) is undefined,
 * and the pickers sort on `label.localeCompare`, so it throws and takes the
 * browse filter, the profile and onboarding with it.
 */
export const CARD_LANGUAGES = [
  'en', 'fr', 'de', 'es', 'it', 'zh-Hans', 'ja', 'pt', 'ru', 'ko', 'zh-Hant',
  'nl', 'pl', 'cs', 'hu', 'id', 'th',
];
/**
 * Which languages each game has EVER been printed in.
 *
 * WHY "EVER" AND NOT "CURRENTLY", which is the whole design. This is a
 * secondhand market: what matters is which cards EXIST, not which a publisher is
 * pressing this year. Magic stopped Russian, Korean and Traditional Chinese in
 * 2022, Portuguese after Modern Horizons 3 and Simplified Chinese with
 * Bloomburrow in 2024 — and every one of those cards is still in somebody's
 * shoebox in Spain. Filtering to today's list would refuse a legitimate card,
 * which is a worse bug than a long dropdown.
 *
 * The cut is still real: One Piece goes from seventeen options to five.
 *
 * THIS SHAPES THE UI AND DOES NOT ENFORCE ANYTHING. The API still accepts every
 * language in the enum for every game, deliberately — a Cardmarket import
 * carries whatever the file says, and rejecting a row because our table
 * disagrees with somebody's export would turn a tidy dropdown into a failed
 * import. Fewer impossible options to pick; no new way to be refused.
 *
 * Kept in step with api/src/lib/games.ts by check-card-languages.mjs, which is
 * the same guard the language list itself already has — three copies of a fact
 * that must agree.
 */
export const GAME_CARD_LANGUAGES = {
  // Bandai, five and only five: en.onepiece-cardgame.com rules announcements.
  onepiece: ['ja', 'en', 'fr', 'zh-Hans', 'ko'],
  /*
   * Magic's six current plus five discontinued. Phyrexian is deliberately absent
   * — it is a handful of promos, it is not one of Cardmarket's seventeen, and
   * the seventeen are where our enum comes from. It belongs in the free-text
   * note until somebody actually needs to filter on it.
   */
  magic: ['en', 'ja', 'fr', 'it', 'de', 'es', 'pt', 'ru', 'ko', 'zh-Hant', 'zh-Hans'],
  // The widest of the five, including Thai and Indonesian, plus Dutch, Polish
  // and Russian which were printed and discontinued.
  pokemon: ['ja', 'en', 'de', 'fr', 'it', 'es', 'pt', 'ko', 'zh-Hant', 'zh-Hans', 'th', 'id', 'nl', 'pl', 'ru'],
  // TCG and OCG together: they are one card pool to somebody holding a card.
  yugioh: ['en', 'fr', 'de', 'it', 'es', 'pt', 'ja', 'ko', 'zh-Hans', 'zh-Hant'],
  lorcana: ['en', 'fr', 'de', 'it', 'es', 'ja', 'zh-Hans'],
  riftbound: ['en', 'zh-Hans', 'fr', 'ko'],
  // CardTrader's own gundam_language: en, jp, zh-CN in our codes.
  gundam: ['en', 'ja', 'zh-Hans'],
};

/**
 * The languages worth offering for a set of games.
 *
 * Empty games means no game chosen, which is every language rather than none:
 * before somebody says what they are selling we cannot know what is impossible,
 * and an empty dropdown is worse than a long one.
 *
 * Several games means the UNION. Browse can filter on more than one at once, and
 * a reader looking at Magic and Pokémon together should see every language
 * either of them prints — an intersection would hide Thai from somebody who
 * ticked Pokémon just because they also ticked Magic.
 */
export function languagesForGames(games) {
  const chosen = (games || []).filter((g) => GAME_CARD_LANGUAGES[g]);
  if (!chosen.length) return CARD_LANGUAGES;
  const union = new Set();
  for (const g of chosen) for (const code of GAME_CARD_LANGUAGES[g]) union.add(code);
  // Ordered by the canonical list rather than by insertion, so the dropdown does
  // not reshuffle when somebody ticks a second game.
  return CARD_LANGUAGES.filter((code) => union.has(code));
}

export const CARD_LANGUAGE_NAMES = {
  en: 'English', fr: 'French', de: 'German', es: 'Spanish', it: 'Italian',
  'zh-Hans': 'Simplified Chinese', ja: 'Japanese', pt: 'Portuguese', ru: 'Russian',
  ko: 'Korean', 'zh-Hant': 'Traditional Chinese',
  nl: 'Dutch', pl: 'Polish', cs: 'Czech', hu: 'Hungarian', id: 'Indonesian', th: 'Thai',
};

/**
 * The legal pages, per locale.
 *
 * They exist in both languages — /privacidad and /condiciones in Spanish,
 * /privacy and /terms in English — and the app footer linked an English reader
 * to the Spanish ones regardless. A policy somebody cannot read is not a policy
 * that was shown to them, which for a privacy notice is the whole point of
 * publishing it.
 *
 * Absolute, to the canonical pages on the main domain.
 */
const LEGAL = {
  'es-ES': [['Privacidad', 'https://cardboardmarket.xyz/privacidad'], ['Condiciones', 'https://cardboardmarket.xyz/condiciones']],
  'en-US': [['Privacy', 'https://cardboardmarket.xyz/privacy'], ['Terms', 'https://cardboardmarket.xyz/terms']],
};
export function legalLinks(locale) { return LEGAL[locale] || LEGAL['es-ES']; }

/*
 * A stored number, shown back readably: `34600000000` -> `+34 600 000 000`.
 *
 * The twin of `displayWhatsApp` in the api. Duplicated rather than fetched
 * because it exists for one purpose — letting somebody SEE the country code that
 * was assumed on their behalf before they agree to publish it — and a formatting
 * helper that needs a round trip cannot do that while they are typing.
 */
export function cbmFormatWhatsApp(number) {
  if (!number) return '';
  if (number.indexOf('34') === 0 && number.length === 11) {
    const local = number.slice(2);
    return '+34 ' + local.slice(0, 3) + ' ' + local.slice(3, 6) + ' ' + local.slice(6);
  }
  return '+' + number;
}

/**
 * The name somebody trades under.
 *
 * A verified store trades as the STORE. That is the whole point of verifying
 * one: a buyer meeting "Asier's LGS Store" at an address is meeting a business,
 * and telling them they are meeting "Asier P." makes the shop's own name a
 * detail buried on a profile.
 *
 * ONE FUNCTION because the alternative is `x.shop ? x.shop.name : x.displayName`
 * written at a dozen call sites — the listing, the seller card, the thread, the
 * trade, the "more from" heading. The trader page had that expression inline
 * and every other screen simply forgot, which is exactly how it drifts: a rule
 * spread across twelve places is twelve chances to be the one that did not get
 * the memo.
 *
 * Takes anything carrying a `displayName` and maybe a `shop` — a listing's
 * seller, a thread's other party, a trade's counterpart. Unverified shops never
 * reach the client, so the presence of one IS the verification.
 */
/**
 * A count, as a number somebody reads rather than one they audit.
 *
 * Asked for: "after we pass 100, 1000 or 10000 it's not really that important
 * if it's 124 or 104923. Let's use the usual K, M postfixes and maybe round to
 * 1 decimal."
 *
 * Which is right, and it is the same argument as the browse cap one layer up:
 * past a certain size a count stops being a quantity and becomes a sense of
 * scale. "104,923" invites somebody to read six digits none of which change
 * what they do next.
 *
 * K AND M IN BOTH LANGUAGES. The first version used `Intl`'s compact notation,
 * which renders Spanish as "5,2 mil" — correct by the CLDR's reckoning, and
 * overruled by the owner, who speaks it: "I believe in spanish we understand K
 * means thousands and M means millions." He is right, and it is the kind of
 * thing a locale database gets to be technically correct about while being
 * wrong in the room.
 *
 * THE SEPARATOR STILL FOLLOWS THE LOCALE, because that half is not a matter of
 * familiarity: "5.2K" reads as five point two to an English speaker and as five
 * thousand two hundred to a Spanish one. So the suffix is fixed and the number
 * in front of it is formatted properly — "5,2K" in Spanish, "5.2K" in English.
 * That is the house rule: date order is a brand decision, separators are the
 * locale's.
 *
 * One decimal below ten, none above, because "124.0K" is a precision the number
 * does not have and "1K" throws away one it does.
 */
const SHORT_UNITS = [[1e9, "B"], [1e6, "M"], [1e3, "K"]];

export function shortCount(n, locale) {
  if (typeof n !== "number" || !Number.isFinite(n)) return n;
  const sign = n < 0 ? "-" : "";
  const size = Math.abs(n);

  for (const [unit, suffix] of SHORT_UNITS) {
    if (size >= unit) {
      const scaled = size / unit;
      return sign + scaled.toLocaleString(locale, {
        maximumFractionDigits: scaled < 10 ? 1 : 0,
      }) + suffix;
    }
  }
  // Under a thousand the digits still mean something, so they are all shown.
  return size.toLocaleString(locale) === String(size) ? String(n) : sign + size.toLocaleString(locale);
}

export function tradingName(party) {
  if (!party) return '';
  return (party.shop && party.shop.name) || party.displayName || '';
}

/**
 * The @ somebody trades under, beside the name they trade under.
 *
 * A store has its own handle now, so a listing by "Cartas del Barrio" shows
 * `@cartas-del-barrio` rather than `@asier-p` — the owner's handle under a
 * store's card was the same mismatch the display name had.
 *
 * Both resolve on the server: the personal handle still reaches the same page,
 * because somebody who runs a store is still a trader and may have links
 * pointing at their own. Only one is SHOWN, and it is the one they trade as.
 *
 * Returns it WITHOUT the @, like the raw field it replaces — every call site
 * writes its own, and a function that sometimes included it would be a
 * different bug at each one.
 */
export function tradingHandle(party) {
  if (!party) return null;
  return (party.shop && party.shop.handle) || party.handle || null;
}
